import { beginViewerNavigation } from "./services/viewerNavigation";
import React, { useCallback, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import * as Sentry from "@sentry/react";

import { downloadFileViewerDataFile } from "@/components/ExcelViewer/services/downloadFileViewerDataFile";
import ViewerImpl from "./ViewerImpl";
import { fileTypeBadge } from "./FileTypeHeaderIcon";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

import { useViewerModeAction } from "../FpPages/ViewerModeToggle";
import { Download, ExternalLink, History, Info, Link } from "lucide-react";
import { useIsPhoneViewport } from "@/hooks/useViewportTier";
import { displayFileName as documentDisplayName } from "@/utils/fileName";
import useDismissOnBack from "@/hooks/useDismissOnBack";
import {
  setFileViewerData,
  setFileViewerDataOnlyNew,
} from "@/redux/fileViewerSlice";
import { useDispatch } from "react-redux";
import { useArtifactPanelActions } from "./ArtifactPanelActions";
import { artifactOriginLabel, useArtifactCitations } from "./ArtifactSources";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import ArtifactPaneHeader, { type PaneAction } from "./ArtifactPaneHeader";
import {
  artifactUrls,
  artifactVersionPreviewUrl,
  type ArtifactVersion,
} from "../FpPages/Artifacts/useArtifacts";
import useFileLoader from "./services/useFileLoader";
import useInteractionByBlobId, {
  interactionViewerKind,
} from "./services/useInteractionByBlobId";
import { isPreviewableUnsupportedExtension } from "../common/FileTree/fileTypeExtensions";
import useDocumentZoom from "../FpPages/RMS/Components/useDocumentZoom";
import ZoomControls from "../FpPages/RMS/Components/ZoomControls";
import DocumentMetadataPanel from "./DocumentMetadataPanel";
import useDocumentMetadata from "./services/useDocumentMetadata";
import OlderVersionNotice from "./OlderVersionNotice";
import openFile from "@/components/FpAppV4/services/openFile";
import DocumentActivityPanel from "./DocumentActivityPanel";
import ArtifactVersionHistoryPanel from "./ArtifactVersionHistoryPanel";
import { useArtifactHistory } from "./artifactHistory";
import {
  captureDocumentViewerEscapePrecedence,
  shouldHandleDocumentViewerEscape,
} from "./documentViewerEscape";
import { useFlash } from "@/hooks/productTimerHooks";
import { dispatchDocumentClose } from "./documentClose";
import { returnToSourceArtifact, sourceTagLabel } from "./sourceOrigin";
import { isDownloadableKind } from "./downloadableKinds";

/** Middle-truncation for the phone header's file name (#11742 round 5
 * T1/T8, CD): the discriminating end of a document name is its TAIL
 * ("…-2026-Q2-final.pdf"), so a tail ellipsis throws away exactly what
 * tells two documents apart. Deliberately different from the record
 * list's two-line clamp — a header is one line. */
export function middleTruncate(name: string, max: number): string {
  if (name.length <= max) return name;
  const tail = Math.min(16, Math.floor(max / 2));
  const head = max - tail - 1;
  return `${name.slice(0, head)}…${name.slice(name.length - tail)}`;
}

interface FileViewerProps {
  variant: "split" | "overlay";
  /** Shown beside the artifact it was cited from (#12452): where the pair
   *  shows is the artifact's toggle's call, so this viewer offers none. */
  besideArtifact?: boolean;
  /** One of several viewers a host arranges, reading an open-file record of
   *  its own (`ScopedFileViewer`, #12481). The host owns what a page-wide
   *  viewer otherwise does for itself: which pane Escape closes (every
   *  mounted viewer listening for it made the OLDEST one win), and telling
   *  the shell a document closed, which would take the shell's own pane
   *  down for a close that was never its (review). */
  hosted?: boolean;
}

const FileViewer = ({
  variant,
  besideArtifact = false,
  hosted = false,
}: FileViewerProps) => {
  const dispatch = useDispatch();
  const [copied, triggerCopied] = useFlash(2000);
  // Details never auto-opens at phone (#11742 round 5 T8, Hunter/CD):
  // the panel took 320 of 390px before the reader saw the document.
  const phone = useIsPhoneViewport();
  const [metadataOpen, setMetadataOpen] = useState(
    variant === "overlay" && !phone
  );
  // Details is its own back level at phone (CD): hardware back exits
  // Details before it exits the viewer — the same dismissal mechanism the
  // viewer itself uses, one level shallower.
  // The document's history (#13433) opens in the slot Details uses, and the
  // two never show together: opening one closes the other. At phone it
  // takes Details' treatment exactly, back dismissal included. The slot is
  // ONE back level whichever panel fills it: switching Details for Activity
  // keeps the entry standing, where a pop for one and a push for the other
  // in the same commit raced and cost the viewer its own level.
  const [activityOpen, setActivityOpen] = useState(false);
  const closeMetadata = useCallback(() => setMetadataOpen(false), []);
  const closeActivity = useCallback(() => setActivityOpen(false), []);
  const closeSidePanel = useCallback(() => {
    setMetadataOpen(false);
    setActivityOpen(false);
  }, []);
  useDismissOnBack(phone && (metadataOpen || activityOpen), closeSidePanel);
  const toggleMetadata = useCallback(() => {
    setActivityOpen(false);
    setMetadataOpen((prev) => !prev);
  }, []);
  const toggleActivity = useCallback(() => {
    setMetadataOpen(false);
    setActivityOpen((prev) => !prev);
  }, []);
  // Crossing INTO the phone tier closes Details (one pane at a time, and
  // T8's no-auto-details) — a rotation must not land on a full-screen
  // Details the reader never asked for. Crossing back up restores the
  // desktop presentation from its own state untouched.
  const wasPhoneRef = React.useRef(phone);
  useEffect(() => {
    if (phone && !wasPhoneRef.current) {
      setMetadataOpen(false);
      setActivityOpen(false);
    }
    wasPhoneRef.current = phone;
  }, [phone]);

  const {
    fileId,
    fileType: fileTypeArg,
    fileName,
    previewUrl,
    downloadUrl,
    artifactId,
    interactionId: noteInteractionId,
    interactionError,
    sourceOf,
  } = useSelector((state: RootState) => state.fileViewerData);
  // Version history presentation (#11819): while it is open the other
  // header actions stand down and the history renders beside the content.
  const history = useArtifactHistory(artifactId);
  // Details and Activity are a plain document's panels. The viewer is not
  // keyed by file, so when an artifact loads into it (a source view's way
  // back) a panel left open would hide the pane at phone and draw nothing in
  // its place. Close both, so the hide and the back entry go with them.
  useEffect(() => {
    if (artifactId) closeSidePanel();
  }, [artifactId, closeSidePanel]);

  const interaction = useInteractionByBlobId(
    fileId,
    !interactionError &&
      (Boolean(noteInteractionId) || fileTypeArg !== "finpilot-interaction"),
    noteInteractionId
  );
  // Explicit context wins over a contextless document association.
  const interactionId = interactionError
    ? null
    : (noteInteractionId ?? interaction?.id ?? null);

  const currentFileId = noteInteractionId
    ? (interaction?.document_id ?? null)
    : fileId;
  const currentType =
    interactionViewerKind(interaction?.document_blob_kind) || fileTypeArg;
  const exactNote =
    Boolean(noteInteractionId) && currentType === "finpilot-interaction";
  // An unreadable file has nothing to preview (#11277); its arm says why.
  const shouldLoad =
    !exactNote &&
    currentType !== "unreadable" &&
    (currentType !== "unsupported" ||
      isPreviewableUnsupportedExtension(fileName));

  const {
    fileType,
    response,
    fileUrl,
    error,
    fileName: loaderFileName,
  } = useFileLoader(currentFileId, currentType, shouldLoad, previewUrl);

  // A chat-produced file always has its original bytes one fetch away, whatever
  // the preview turned out to be — so downloading it never depends on the kind.
  // An exact note-only selection exports through its authorized PDF endpoint.
  const canDownload =
    !interactionError &&
    Boolean(
      exactNote ||
        (!currentFileId && downloadUrl) ||
        (currentFileId && isDownloadableKind(fileType))
    );

  const downloadOpenFile = () =>
    downloadFileViewerDataFile({
      fileId: currentFileId,
      fileType: currentType,
      // A different current document supplies its own download filename.
      // Keep caller-title naming only when it still describes this file.
      fileName: currentFileId === fileId ? fileName : null,
      downloadUrl,
      interactionId: noteInteractionId,
    });

  // What the viewer's own keyboard save (Ctrl/Cmd-S in the PDF reader) runs:
  // exactly the header's Download, so none while version history is open,
  // where the header offers none (it would save the live file, not the
  // version on screen).
  const viewerDownload =
    canDownload && !history.open ? downloadOpenFile : undefined;

  // Keep the selected interaction title and the shared document naming rules.
  const rawFileName = interaction?.title || fileName || loaderFileName || "";
  const displayFileName = documentDisplayName(rawFileName);

  // The open document's metadata, read for its lineage (#13681): a document
  // that is not its lineage's current content says so in a bar pinned over
  // the document, outside Details. The same read the Details panel makes for
  // a plain document, so react-query serves both from one request.
  const { data: metadata } = useDocumentMetadata(currentFileId, !artifactId);
  // The pane the bar is pinned in.
  const [documentPane, setDocumentPane] = useState<HTMLDivElement | null>(null);
  // Opens the latest in this viewer, as a click on its Versions row does.
  // The bar sits in the document pane, which a phone hides while Details
  // covers it, so there is no phone Details pane to close from here.
  const openLatest = useCallback(
    (latest: { blob_id: string; filename: string; blob_kind: string }) =>
      openFile({
        fileId: latest.blob_id,
        fileName: latest.filename,
        fileType: latest.blob_kind,
      }),
    []
  );

  const { zoomLevel, isZoomable, zoomIn, zoomOut, resetZoom } = useDocumentZoom(
    { fileId: currentFileId || "", fileType }
  );

  const handleCopyLink = useCallback(() => {
    if (!currentFileId) return;
    const link = new URL("/documents", window.location.origin);
    link.searchParams.set("docId", currentFileId);
    if (noteInteractionId)
      link.searchParams.set("interactionId", noteInteractionId);
    navigator.clipboard.writeText(link.toString()).then(() => triggerCopied());
  }, [currentFileId, noteInteractionId, triggerCopied]);

  const handleClose = useCallback(() => {
    // A hosted viewer closes its own record and nothing else: the page-wide
    // navigation counter and the shell's document-close are not its to move.
    if (!hosted) beginViewerNavigation();
    dispatch(setFileViewerDataOnlyNew({ isOpen: false }));
    // Marked as a document's own close (#12452): the drawer keeps an
    // artifact it shows beside this document, and that artifact's history.
    if (!hosted) dispatchDocumentClose(artifactId ?? null);

    // Clean up docId from URL when closing file viewer. Carries
    // history.state forward (#11742): a bare `{}` wipes the phone overlay's
    // back-dismissal marker and react-router's idx bookkeeping. A hosted
    // viewer that does not own the record leaves the owner's URL alone.
    const currentUrl = new URL(window.location.href);
    if (!hosted && currentUrl.searchParams.has("docId")) {
      currentUrl.searchParams.delete("docId");
      currentUrl.searchParams.delete("interactionId");
      window.history.replaceState(
        window.history.state ?? {},
        "",
        currentUrl.toString()
      );
    }
  }, [dispatch, artifactId, hosted]);

  useEffect(() => {
    // The host arranges Escape among its viewers.
    if (hosted) return undefined;
    const captureEscapePrecedence = (event: KeyboardEvent) =>
      captureDocumentViewerEscapePrecedence(event, document);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!shouldHandleDocumentViewerEscape(event, document)) return;

      event.preventDefault();
      event.stopPropagation();
      // A source view steps back to the artifact it was cited from; the X
      // is what closes the pane.
      if (sourceOf) returnToSourceArtifact(sourceOf);
      else handleClose();
    };

    // Capture snapshots precedence; bubble lets nested handlers run first.
    document.addEventListener("keydown", captureEscapePrecedence, true);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", captureEscapePrecedence, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleClose, hosted, sourceOf]);

  // The shared action list (#13261): Save, Chat, Share, Download, Copy
  // link, Link to record for an artifact-backed file; Download alone for a
  // plain document. Download runs the one handler the viewer's own save
  // runs too (rules/frontend.md, Download and Export).
  const { actions: artifactActions, dialogs: artifactDialogs } =
    useArtifactPanelActions({
      artifactId: artifactId ?? null,
      title: displayFileName,
      file: {
        key: "download",
        label: "Download",
        icon: Download,
        disabled: !canDownload,
        onSelect: downloadOpenFile,
      },
    });
  // Where an artifact-backed file came from and when it changed, for the
  // head of the title's menu, as every artifact pane states them.
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const citations = useArtifactCitations(artifactId);
  const queryClient = useQueryClient();
  // An artifact-backed file uses the artifact-scoped mode (its own memory,
  // split by default); a plain document keeps the per-surface choice (#9746).
  const viewerModeAction = useViewerModeAction(Boolean(artifactId));
  // Viewing a version swaps the preview to its snapshot; the current
  // version points it back at the live file (#11819).
  const viewVersion = (version: ArtifactVersion | null) => {
    if (!artifactId) return;
    if (version === null) {
      dispatch(
        setFileViewerData({ previewUrl: artifactUrls(artifactId).previewUrl })
      );
    } else if (version.has_file) {
      dispatch(
        setFileViewerData({
          previewUrl: artifactVersionPreviewUrl(
            artifactId,
            version.version_number
          ),
        })
      );
    }
  };
  const interactionPage = interactionId
    ? `/rms/interaction/${interactionId}`
    : null;
  const headerActions: PaneAction[] = [
    ...artifactActions,
    // The way to the full interaction, in the row where it is seen (#14869,
    // Hunter: hiding it in the title's menu "was an extremely bad idea"):
    // a link, so a plain click goes there in this tab and a modifier click
    // opens a new one. Its icon alone at phone, never in the menu.
    ...(!history.open && interactionPage
      ? [
          {
            key: "open-interaction",
            label: "Open interaction",
            icon: ExternalLink,
            word: true,
            buttonOnly: true,
            href: interactionPage,
            onSelect: () => navigate(interactionPage),
          } satisfies PaneAction,
        ]
      : []),
    // Copy link lives in the title's menu (#12481) and stands down while
    // version history is open (#11819).
    // A plain document's Copy link is the docId link; an artifact-backed
    // file gets the artifact link from its own actions.
    ...(!history.open && !artifactId
      ? [
          {
            key: "copy-link",
            label: "Copy link",
            hint: copied ? "Copied!" : "Copy link",
            icon: Link,
            disabled: !currentFileId,
            secondary: true,
            onSelect: handleCopyLink,
          } satisfies PaneAction,
        ]
      : []),
    // Activity — a plain document's history (#13433), in a section of its
    // own under Download and Copy link, where a draft's Version history sits.
    ...(!history.open && !artifactId
      ? [
          {
            key: "activity",
            label: "Activity",
            icon: History,
            active: activityOpen,
            disabled: !currentFileId,
            secondary: true,
            dividerBefore: true,
            onSelect: toggleActivity,
          } satisfies PaneAction,
        ]
      : []),
    // Details — documents only. An artifact has no blob metadata panel
    // behind this toggle, so the button did nothing there (Hunter, #11548
    // feedback 2026-09-01). About the pane, so it sits with the mode toggle.
    ...(!artifactId
      ? [
          {
            key: "details",
            label: metadataOpen ? "Hide details" : "Show details",
            icon: Info,
            active: metadataOpen,
            disabled: !currentFileId,
            trailing: true,
            onSelect: toggleMetadata,
          } satisfies PaneAction,
        ]
      : []),
    ...(!history.open && !besideArtifact && viewerModeAction
      ? [viewerModeAction]
      : []),
  ];

  return (
    <div className="flex flex-col h-full" data-file-viewer="">
      {/* The one header every pane wears (#11548 r51, #12481, Gerry: "make
          the file viewer use the same header component"): the type tile and
          the name, the title's menu, then the pane's controls, Close last.
          It folds as the pane narrows. */}
      <ArtifactPaneHeader
        title={phone ? middleTruncate(displayFileName, 34) : displayFileName}
        // The file's own name, not the name printed beside it: an
        // interaction's `.fp` is stripped for display, and the title left
        // behind is free text that may well end in ".pdf".
        badge={fileTypeBadge(rawFileName, fileType, response?.contentType)}
        returnTo={
          sourceOf
            ? {
                title: sourceOf.title,
                badge: sourceOf.badge,
                onReturn: () => returnToSourceArtifact(sourceOf),
              }
            : undefined
        }
        tag={sourceOf ? sourceTagLabel(sourceOf) : undefined}
        origin={artifactId ? artifactOriginLabel(citations, pathname) : null}
        updatedAt={artifactId ? citations.updatedAt : null}
        version={
          artifactId
            ? {
                viewing: null,
                onView: viewVersion,
                historyOpen: history.open,
                onToggleHistory: history.toggle,
              }
            : undefined
        }
        onClose={handleClose}
        aids={
          // Reading zoom, in the header where it covers nothing (#11852).
          // Not gated on version history: the content stays scaled while
          // history is open, so standing the control down would strand the
          // reader at whatever level they had set.
          isZoomable ? (
            <ZoomControls
              zoomLevel={zoomLevel}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
              onReset={resetZoom}
            />
          ) : undefined
        }
        actions={headerActions}
      >
        {artifactDialogs}
      </ArtifactPaneHeader>
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-3">
          <div className="text-red-800 font-semibold">
            Error loading document
          </div>
        </div>
      )}
      <div className="flex-1 h-0 flex">
        <div
          ref={setDocumentPane}
          className={`relative flex-1 min-w-0 ${
            phone && (metadataOpen || activityOpen) ? "hidden" : ""
          }`}
          data-file-viewer-pane=""
        >
          {/* Pinned over the document, outside Details, which a record's
              pane keeps closed: an earlier version says so whatever the
              panel state. In the pane, not in its scroll container, so it
              stays put while the document scrolls. Mounted for every plain
              document, so its live region is on the page before the bar is
              drawn into it. */}
          {!artifactId && (
            <OlderVersionNotice
              fileId={currentFileId}
              lineage={metadata?.lineage}
              pane={documentPane}
              onOpenLatest={openLatest}
              // Only while Details is closed, where the toggle opens it.
              onShowDetails={metadataOpen ? undefined : toggleMetadata}
            />
          )}
          <div className="h-full overflow-auto">
            <ErrorBoundary
              onError={(error, info) => {
                Sentry.captureException(error, {
                  extra: {
                    componentStack: info.componentStack,
                    fileId: currentFileId,
                    fileType,
                  },
                });
              }}
              fallback={
                <div className="text-gray-500 p-8">
                  <div className="text-lg font-bold">
                    Can not preview this file
                  </div>
                  {canDownload && (
                    <span className="text-sm">
                      <span
                        className="text-blue-700 cursor-pointer underline mr-1"
                        onClick={() => downloadOpenFile()}
                      >
                        Download
                      </span>
                      to open the file.
                    </span>
                  )}
                </div>
              }
            >
              {isZoomable ? (
                <div style={{ zoom: zoomLevel / 100 }}>
                  <ViewerImpl
                    key={
                      [currentFileId, previewUrl, noteInteractionId].join(
                        ":"
                      ) || noteInteractionId
                    }
                    onEscape={handleClose}
                    onDownload={viewerDownload}
                  />
                </div>
              ) : (
                <ViewerImpl
                  key={
                    [currentFileId, previewUrl, noteInteractionId].join(":") ||
                    noteInteractionId
                  }
                  onEscape={handleClose}
                  onDownload={viewerDownload}
                />
              )}
            </ErrorBoundary>
          </div>
        </div>
        {/* Version history as a column beside the content (#11819); viewing
            a version swaps the preview to its snapshot, restoring points it
            back at the live file. */}
        {history.open && artifactId && (
          <ArtifactVersionHistoryPanel
            artifactId={artifactId}
            onView={viewVersion}
            onRestored={(_version, artifact) => {
              // The live URL's cached bytes are the ones the restore just
              // replaced: dropped, so showing the current version again
              // reads the restored file rather than drawing the old one
              // first (#13261).
              queryClient.removeQueries({
                queryKey: ["fp0065", artifactUrls(artifactId).previewUrl],
              });
              dispatch(
                setFileViewerData({
                  // Cache-bust: the live URL is unchanged but its bytes
                  // just were. A cross-type restore also renames the
                  // artifact (pdf → docx), so the shown name follows the
                  // refreshed item — otherwise Download hands over docx
                  // bytes under the stale pdf name.
                  fileName: artifact.filename ?? artifact.title,
                  previewUrl: `${artifactUrls(artifactId).previewUrl}?r=${Date.now()}`,
                })
              );
            }}
          />
        )}
        {/* Artifacts have no blob metadata behind Details (#11819): the
            pane opened empty beside every artifact in the overlay, whose
            default is Details-open. The toggle is already hidden for them
            above; the pane follows. */}
        {metadataOpen && !artifactId && (
          // At phone Details is a full-width pane REPLACING the document
          // (one-pane-at-a-time; the document stays mounted above so its
          // scroll position survives), with its own scroll and wrapping
          // long values (#11742 round 5 T1, CD (a)).
          <div
            className={
              phone
                ? "flex-1 min-w-0 overflow-y-auto break-words"
                : "w-[320px] shrink-0"
            }
          >
            <DocumentMetadataPanel
              blobId={currentFileId}
              onClose={() => setMetadataOpen(false)}
              // Opening another version from Versions swaps the document
              // beneath the panel; at phone width the panel is covering it,
              // so it closes to show what was just opened. Desktop keeps
              // the panel beside the document.
              onOpenVersion={phone ? closeMetadata : undefined}
            />
          </div>
        )}
        {/* The document's history, in Details' slot and with its phone
            treatment: beside the document on desktop, replacing it at
            phone (#13433). */}
        {activityOpen && !artifactId && (
          <div
            className={
              phone
                ? "flex-1 min-w-0 overflow-hidden break-words"
                : "w-[320px] shrink-0"
            }
          >
            <DocumentActivityPanel
              blobId={currentFileId}
              onClose={closeActivity}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FileViewer;
